import { LocaleContext, LocationContext } from "contexts"
import { useContext } from "react"
import { useStaticQuery, graphql } from "gatsby"
import Slider from "react-slick"
import LocalizedLink from "components/LocalizedLink"
import cc from "classcat"
import useTranslations from "hooks/useTranslations"
import { removeLocaleFromPath } from "utils/locale"
import { sliderSettings } from "components/atoms/SliderSettings"
import { CardHover } from "../../atoms/common/CardHover"

const Tag = ({ theme, tag }) => {
  return (
    <div className="my-2 inline-block ">
      <CardHover
        className="rounded-full duration-100 hover:-translate-y-1"
        shadow=""
      >
        <p
          className={cc([
            "m-0 rounded-full px-4 py-2 text-sm font-bold",
            {
              "bg-kkm-green text-black": theme === "dark",
              "bg-white text-zinc-800": ["light", "yellow", "red"].includes(
                theme,
              ),
              "bg-zinc-800 text-zinc-100": theme === "green",
            },
          ])}
        >
          {tag}
        </p>
      </CardHover>
    </div>
  )
}

const BlogPostCard = ({ post, id, theme }) => {
  const { locale } = useContext(LocaleContext)
  const publicationDate = new Date(post.publication_date)
  const publicationDateString = publicationDate.toLocaleString(locale, {
    dateStyle: "medium",
  })
  const slug = `/blog/${post?.slug?.text}/`
  return (
    <LocalizedLink
      id={id}
      to={slug}
      className="mx-auto mb-6 flex w-full cursor-pointer flex-col space-y-6 pr-4 md:px-0"
    >
      <CardHover className="rounded-lg">
        <div className="flex shrink-0 ">
          <img
            className="aspect-video w-full rounded-lg object-cover "
            src={post.image_main.url}
            alt=""
          />
        </div>
      </CardHover>
      <div className="flex flex-1 flex-col justify-between ">
        <div className="flex-1 text-left ">
          <Tag tag={post.tags} theme={theme} />
          <div className="mt-2 block">
            <p
              className={cc([
                "m-0 font-display text-2xl font-semibold",
                {
                  "text-white": theme === "dark",
                  "text-zinc-800": ["green", "light", "yellow", "red"].includes(
                    theme,
                  ),
                },
              ])}
            >
              {post.title.text}
            </p>
          </div>
        </div>
        <div className="mt-6 flex items-center">
          <div
            className={cc([
              "flex space-x-1 text-base",
              {
                "text-zinc-200": theme === "dark",
                "text-zinc-800": theme === "light",
                "text-zinc-700": ["green", "yellow", "red"].includes(theme),
              },
            ])}
          >
            <time dateTime={publicationDateString}>
              {publicationDateString}
            </time>
          </div>
        </div>
      </div>
    </LocalizedLink>
  )
}

type BlogPostsProps = { theme?: "dark" | "light" | "green" | "yellow" | "red" }

const BlogPosts = ({ theme = "dark" }: BlogPostsProps) => {
  const { locale } = useContext(LocaleContext)
  const { location } = useContext(LocationContext)
  const pageName = removeLocaleFromPath(location.pathname, locale)
  const { blogSectionTitle } = useTranslations()
  const query = useStaticQuery(graphql`
    query LastBlogPostsQuery {
      allPrismicBlogPost(sort: { data: { publication_date: DESC } }) {
        nodes {
          id
          lang
          data {
            image_main {
              url
            }
            publication_date(formatString: "MMMM DD, YYYY")
            show_in_menu
            spoiler {
              text
            }
            tags
            title {
              text
            }
            slug {
              text
            }
            hide_from_pages {
              page
            }
          }
        }
      }
    }
  `)

  const seenSlugs = new Set<string>()
  const lastBlogPostsForLocale = query.allPrismicBlogPost.nodes
    .filter(({ lang }) =>
      locale === "en-fi"
        ? lang === locale || lang === "fi-fi"
        : lang === locale,
    )
    .filter(({ data }) => data.publication_date && data.title?.text)
    .filter(({ data }) => {
      const slug = data.slug?.text
      if (!slug || seenSlugs.has(slug)) return false
      seenSlugs.add(slug)
      return true
    })
    .filter(
      ({ data }) =>
        data.hide_from_pages.findIndex(({ page }) => page === pageName) === -1,
    )
    .sort((postA, postB) => {
      // Add a custom sorting condition based on locale
      if (locale === "en-fi") {
        if (postA.lang === "en-fi" && postB.lang === "fi-fi") {
          return -1 // English posts come before Finnish posts
        } else if (postA.lang === "fi-fi" && postB.lang === "en-fi") {
          return 1 // Finnish posts come after English posts
        }
      }
      return 0 // No specific order for other cases
    })
    .slice(0, 3)

  let dotColor

  switch (theme) {
    case "dark":
      dotColor = "white-on-black"
      break
    case "light":
      dotColor = "dark-on-white"
      break
    case "green":
      dotColor = "black-on-green"
      break
    case "yellow":
      dotColor = "black-on-yellow"
      break
    case "red":
      dotColor = "black-on-red"
      break
    default:
      dotColor = "white-on-black"
  }

  return (
    <div
      className={cc([
        "relative pb-20 pt-16 lg:py-24",
        {
          "bg-zinc-800": theme === "dark",
          "bg-white": theme === "light",
          "bg-kkm-green": theme === "green",
          "bg-kkm-yellow": theme === "yellow",
          "bg-kkm-red": theme === "red",
        },
      ])}
    >
      <div className="relative mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="max-w-md px-4 text-left md:px-0">
          <h2
            className={cc([
              "text-3xl font-extrabold tracking-tight sm:text-4xl",
              {
                "text-neutral-100": ["dark", "green"].includes(theme),
                "text-neutral-800": ["light", "yellow", "red"].includes(theme),
              },
            ])}
          >
            {blogSectionTitle}
          </h2>
        </div>
        <div className="mt-12">
          <div
            className={cc([
              "hidden gap-16 md:grid md:grid-cols-2 lg:grid-cols-3",
              lastBlogPostsForLocale.length < 3
                ? "lg:grid-cols-2"
                : "lg:grid-cols-3",
            ])}
          >
            {lastBlogPostsForLocale.map((post, index) => (
              <BlogPostCard
                post={post.data}
                id={post.id}
                key={post.id}
                theme={theme}
              />
            ))}
          </div>
          <div className="block md:hidden ">
            <div className="ml-4">
              <Slider slidesToShow={1} {...sliderSettings(dotColor)}>
                {lastBlogPostsForLocale.map((post, index) => (
                  <BlogPostCard
                    post={post.data}
                    id={post.id}
                    key={post.id}
                    theme={theme}
                  />
                ))}
              </Slider>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BlogPosts
